import clsx from "clsx";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import styles from "./controls.module.css";

// Кнопки и переключатели. Всё остальное в проекте опирается на эти компоненты,
// собственных <button> в приложениях быть не должно.

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: ReactNode;
  loading?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  block,
  icon,
  loading,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={clsx(
        styles.button,
        styles[variant],
        size === "sm" && styles.sizeSm,
        size === "lg" && styles.sizeLg,
        block && styles.block,
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner size={16} /> : icon}
      {children}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  size?: "sm" | "md";
}

export function IconButton({ label, size = "md", className, children, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={clsx(styles.iconButton, size === "sm" && styles.iconSm, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: ReactNode;
}

export function Switch({ label, className, ...rest }: SwitchProps) {
  return (
    <label className={clsx(styles.switch, className)}>
      <input type="checkbox" className={styles.switchInput} {...rest} />
      <span className={styles.switchTrack}>
        <span className={styles.switchThumb} />
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: ReactNode;
}

export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={clsx(styles.check, className)}>
      <input type="checkbox" className={styles.checkBox} {...rest} />
      {label ? <span>{label}</span> : null}
    </label>
  );
}

export function Radio({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={clsx(styles.check, className)}>
      <input type="radio" className={styles.checkBox} {...rest} />
      {label ? <span>{label}</span> : null}
    </label>
  );
}

export interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  showValue?: boolean;
  formatValue?: (value: number) => string;
}

export function Slider({ showValue = true, formatValue, className, value, ...rest }: SliderProps) {
  const numeric = Number(value ?? 0);
  return (
    <span className={clsx(styles.slider, className)}>
      <input type="range" className={styles.sliderInput} value={value} {...rest} />
      {showValue ? (
        <span className={styles.sliderValue}>{formatValue ? formatValue(numeric) : numeric}</span>
      ) : null}
    </span>
  );
}

export interface SegmentedProps<T extends string> {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (value: T) => void;
  className?: string;
}

/** Переключатель режимов: «неделя / всё время», «черновики / отправленные» */
export function Segmented<T extends string>({ value, options, onChange, className }: SegmentedProps<T>) {
  return (
    <div className={clsx(styles.group, className)} role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          className={clsx(styles.groupItem, option.value === value && styles.groupItemActive)}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ animation: "mnk-spin 0.9s linear infinite" }}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <style>{"@keyframes mnk-spin { to { transform: rotate(360deg) } }"}</style>
    </svg>
  );
}
